import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth'

/*
 * Read the signed-in user's attributes, whichever kind of sign-in produced them (#671).
 *
 * `fetchUserAttributes` calls Cognito's `GetUser` API, which needs the
 * `aws.cognito.signin.user.admin` scope on the access token. Tokens from the Hosted UI - so every
 * Microsoft sign-in - carry only `openid email profile`, so the call fails with
 *
 *   NotAuthorizedException: Access Token does not have required scopes
 *
 * which surfaced as "I cannot make bookings any more": saving a booking refreshes the user, the
 * refresh threw, and the save with it. Password sign-ins were unaffected, which is why it went
 * unnoticed until someone used the button we had just shipped.
 *
 * The ID token already carries what the app reads - email, name, and the `custom:customer` claim -
 * so it is a complete fallback. `profile` (the job title) is not guaranteed there, so a federated
 * user may have an empty job title on first load; that is cosmetic and fixable by editing the
 * profile, whereas a thrown error stops the person working.
 */
const claimsFromSession = async () => {
  try {
    const session = await fetchAuthSession()
    return (session && session.tokens && session.tokens.idToken && session.tokens.idToken.payload) || {}
  } catch (err) {
    return {}
  }
}

const attributesFromClaims = claims => {
  const attributes = {}

  for (const key of ['email', 'email_verified', 'name', 'given_name', 'family_name', 'custom:customer', 'identities']) {
    if (claims[key] !== undefined && claims[key] !== null) {
      attributes[key] = claims[key]
    }
  }

  return attributes
}

/*
 * A federated session cannot call GetUser at all: the token has no
 * `aws.cognito.signin.user.admin` scope, so the request is answered 400 and logged in the browser
 * console on every refresh. Asking anyway and catching the failure worked, but it left a red
 * error in the console each time a booking was saved - noise a person reads as "still broken".
 *
 * So decide before asking. The ID token says which kind of session this is: a federated one has an
 * `identities` claim, and its username is `<Provider>_<sub>`.
 */
const isFederated = claims =>
  Boolean(claims && (claims.identities || String(claims['cognito:username'] || '').indexOf('_') > 0))

const loadUserAttributes = async () => {
  const claims = await claimsFromSession()
  if (isFederated(claims)) {
    return attributesFromClaims(claims)
  }

  try {
    return await fetchUserAttributes()
  } catch (err) {
    if (err && err.name === 'NotAuthorizedException') {
      // A password session that still cannot ask: the claims are the best answer we have.
      return attributesFromClaims(claims)
    }

    throw err
  }
}

export { loadUserAttributes, attributesFromClaims, isFederated }
